import { useCallback } from "react";
import { useUpdateProductMutation } from "src/api/services/productsApi";
import { InputType, Product } from "src/api/types";
import CategoriesSelect from "src/components/CategoriesSelect";
import { createDrawerProvider } from "src/utils/createDrawerProvider";

const { DrawerProvider, useDrawerContext } =
  createDrawerProvider<Product>("Update Product");

const ProductsDrawerProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [updateProduct, { isLoading }] = useUpdateProductMutation();

  const onSubmit = useCallback(
    (item: Product) => {
      const { category, gender, ...rest } = item;

      updateProduct({
        category_id: category.id,
        gender: gender === "Unisex" ? null : gender,
        ...rest,
      });
    },
    [updateProduct],
  );

  const getInputs = useCallback(
    (
      item: Product,
      setItem: (key: keyof Product, value: Product[keyof Product]) => void,
    ): InputType<Product>[] => [
      {
        type: "text",
        name: "slug",
        label: "Slug",
        required: true,
        value: item.slug,
        onChange: (v) => setItem("slug", v),
      },
      {
        type: "text",
        name: "name",
        label: "Name",
        required: true,
        value: item.name,
        onChange: (v) => setItem("name", v),
      },
      {
        type: "text",
        name: "display_name",
        label: "Display name",
        required: false,
        value: item.display_name,
        onChange: (v) => setItem("display_name", v),
      },
      {
        type: "select",
        options: [
          { label: "Subscription", value: "subscription" },
          {
            label: "One time",
            value: "one_time",
          },
        ],
        label: "Billing model",
        required: true,
        value: item.billing_model,
        onChange: (v) => setItem("billing_model", v),
      },
      {
        type: "custom",
        label: "Category",
        component: () => (
          <CategoriesSelect
            select={(c) => ({ id: c.id, name: c.name })}
            value={item.category}
            onChange={(v) => setItem("category", v)}
            required
          />
        ),
      },
      {
        type: "select",
        options: [
          { label: "Unisex", value: "Unisex" },
          { label: "Male", value: "Male" },
          {
            label: "Female",
            value: "Female",
          },
        ],
        label: "Gender",
        required: false,
        value: item.gender ?? "",
        onChange: (v) => setItem("gender", v),
      },
      {
        type: "checkbox",
        label: "Is GLP-1",
        onChange: (v) => setItem("is_glp1", v),
        value: item.is_glp1,
        required: false,
      },
      {
        type: "checkbox",
        label: "Insurance Based",
        onChange: (v) => setItem("insurance_based", v),
        value: item.insurance_based,
        required: false,
      },
      {
        type: "checkbox",
        label: "Is E-Prescribe",
        onChange: (v) => setItem("is_e_prescribe", v),
        value: item.is_e_prescribe,
        required: false,
      },
    ],
    [],
  );

  return (
    <DrawerProvider
      getInputs={getInputs}
      loading={isLoading}
      onSubmit={onSubmit}
    >
      {children}
    </DrawerProvider>
  );
};

export default ProductsDrawerProvider;
export { useDrawerContext };
